import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

type ToastType = 'loading' | 'error' | 'success';

type ToastState = {
  type: ToastType | null;
  message: string;
  isVisible: boolean;
};

const INITIAL_TOAST: ToastState = { type: null, message: '', isVisible: false };

type ToastContextValue = {
  notification: ToastState;
  showLoading: (message?: string) => void;
  showError: (message: string, durationMs?: number) => void;
  showSuccess: (message: string, durationMs?: number) => void;
  hideNotification: () => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toast, setToast] = useState<ToastState>(INITIAL_TOAST);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearToastTimer = useCallback(() => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
  }, []);

  const hideNotification = useCallback(() => {
    clearToastTimer();
    setToast(INITIAL_TOAST);
  }, [clearToastTimer]);

  const showToast = useCallback(
    (type: ToastType, message: string, durationMs?: number) => {
      clearToastTimer();
      setToast({ type, message, isVisible: true });
      if (durationMs && durationMs > 0) {
        toastTimerRef.current = setTimeout(() => {
          setToast(INITIAL_TOAST);
        }, durationMs);
      }
    },
    [clearToastTimer]
  );

  const showLoading = useCallback(
    (message = 'Preparing your data') => showToast('loading', message),
    [showToast]
  );

  const showError = useCallback(
    (message: string, durationMs = 3000) => {
      showToast('error', message, durationMs);
      import('../platform/haptics').then(h => h.hapticError());
    },
    [showToast]
  );

  const showSuccess = useCallback(
    (message: string, durationMs = 3000) => showToast('success', message, durationMs),
    [showToast]
  );

  useEffect(() => clearToastTimer, [clearToastTimer]);

  // Listen for global toast events from non-React code
  useEffect(() => {
    const handler = (e: Event) => {
      const { type, message } = (e as CustomEvent<{ type: 'success' | 'error'; message: string }>).detail;
      showToast(type, message, 3000);
    };
    window.addEventListener('global-toast', handler);
    return () => window.removeEventListener('global-toast', handler);
  }, [showToast]);

  const value = useMemo<ToastContextValue>(
    () => ({
      notification: toast,
      showLoading,
      showError,
      showSuccess,
      hideNotification,
    }),
    [toast, showLoading, showError, showSuccess, hideNotification]
  );

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

/** Fire-and-forget toast from non-React code (API layer, utils, etc.). */
export function emitToast(type: 'success' | 'error', message: string): void {
  window.dispatchEvent(new CustomEvent('global-toast', { detail: { type, message } }));
}
